import { z } from 'zod'
import { Mutator } from './base'
import { DataBase } from '@/lib/kysely'
import { useUserStore } from '@/stores/user'
import type { InsertQuery, UpdateQuery, DeleteQuery } from '@/lib/entity/base'

import { graphql } from '@/gql'
import type { Coassign_Holiday_Insert_Input, Coassign_Holiday_Set_Input } from '@/gql/graphql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import { formatISO } from 'date-fns'
import { pick } from '@/lib/utils/functional'
import { type Reactive } from 'vue'

// yyyy-MM-dd形式＆1980〜2099年の間であること
const regex = /^(19[8-9]\d|20\d\d)-\d{2}-\d{2}$/

export const holidaySchema = z.object({
  id: z.string().max(21),
  name: z.string().trim().max(10),
  date: z.string().regex(regex)
})

export type Holiday = z.infer<typeof holidaySchema>

export class HolidayMutator extends Mutator<typeof holidaySchema> {
  static readonly stageableClassName = 'HolidayMutator'
  schema = holidaySchema
  table = 'holiday' as const

  static async load(id: string): Promise<Reactive<HolidayMutator>> {
    const self = this.createInstance()
    // FIXME: need use watch query
    const base = await DataBase.selectFrom(self.table)
      .selectAll()
      .where('id', '=', id)
      .executeTakeFirst()

    self.context = { id }
    self.base = base
    return self
  }

  async updateLocalDB(): Promise<UpdateQuery[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    return [DataBase.updateTable(this.table).set(params).where('id', '=', id).compile()]
  }

  async insertLocalDB(): Promise<InsertQuery[]> {
    const tenant_id = useUserStore().userInfo!.tenantId
    const params = { ...(await this.parsed)!, ...{ tenant_id } }
    return [DataBase.insertInto(this.table).values(params).compile()]
  }

  async deleteLocalDB(): Promise<DeleteQuery[]> {
    const id = this.context.id!
    return [DataBase.deleteFrom(this.table).where('id', '=', id).compile()]
  }

  async asInsertMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const params = (await this.parsed)!
    const variables: Coassign_Holiday_Insert_Input = { ...params, ...{ commit_id } }
    return [{ mutation: insertHolidayMutationDocument, variables: { input: variables } }]
  }

  async asDeleteMutation(commitId: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const variables: Coassign_Holiday_Set_Input = {
      commit_id: commitId,
      deleted_at: formatISO(new Date())
    }
    return [{ mutation: updateHolidayMutationDocument, variables: { id: id, input: variables } }]
  }

  async asUpdateMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    const variables: Coassign_Holiday_Set_Input = { ...params, ...{ commit_id } }
    return [{ mutation: updateHolidayMutationDocument, variables: { id: id, input: variables } }]
  }
}

const insertHolidayMutationDocument = graphql(/* GraphQL */ `
  mutation insertHoliday($input: coassign_holiday_insert_input!) {
    insert_coassign_holiday_one(object: $input) {
      created_at
    }
  }
`)

const updateHolidayMutationDocument = graphql(/* GraphQL */ `
  mutation updateHoliday($id: String!, $input: coassign_holiday_set_input!) {
    update_coassign_holiday_by_pk(pk_columns: { id: $id }, _set: $input) {
      updated_at
    }
  }
`)
